/* Pristine drawer: server-rendered prices and plans; native cart is the fallback. */
(() => {
  'use strict';
  const root = document.querySelector('[data-smart-cart]');
  if (!root || root.hasAttribute('data-disabled')) return;
  const panel = root.querySelector('[role="dialog"]');
  const base = window.Shopify?.routes?.root || '/';
  const request = window.fetch.bind(window);
  let busy = false, previousFocus, inerted = [], lastRefresh = 0;
  const recommendationCache = new Map();
  const declinedGifts = new Set();
  const retired = window.Rebuy?.SmartCart;
  if (retired?.status === 'ready' && typeof retired.destroy === 'function') retired.destroy();
  const config = () => JSON.parse(root.querySelector('[data-sc-config]').textContent);
  function message(text, error = false) {
    const el = root.querySelector(error ? '[data-sc-error]' : '[data-sc-status]');
    if (el) { el.textContent = text; el.hidden = !text; }
  }
  function open() {
    if (root.classList.contains('is-open')) return;
    previousFocus = document.activeElement;
    panel.showModal();
    root.classList.add('is-open'); root.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('.js-cart-drawer-toggle').forEach(el => el.setAttribute('aria-expanded','true'));
    document.documentElement.classList.add('smart-cart-open');
    root.querySelector('.smart-cart__scroll').scrollTop = 0;
    let node = root;
    while (node && node !== document.body) {
      [...node.parentElement.children].forEach(sibling => {
        if (sibling !== node && !sibling.inert && !['SCRIPT','STYLE','LINK'].includes(sibling.tagName)) {
          sibling.inert = true; inerted.push(sibling);
        }
      });
      node = node.parentElement;
    }
    root.querySelector('button[data-sc-close]')?.focus();
  }
  function close() {
    panel.close();
    root.classList.remove('is-open'); root.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('.js-cart-drawer-toggle').forEach(el => el.setAttribute('aria-expanded','false'));
    document.documentElement.classList.remove('smart-cart-open');
    inerted.forEach(el => { el.inert = false; }); inerted = [];
    if (previousFocus?.isConnected) previousFocus.focus();
  }
  function setBusy(value) {
    busy = value; root.classList.toggle('is-loading', value);
    panel.setAttribute('aria-busy', String(value));
    root.querySelectorAll('[data-sc-qty],[data-sc-add],[data-sc-apply-plan],[data-sc-plan],[data-sc-remove-gift],button[name="checkout"]').forEach(el => { el.disabled = value; });
  }
  async function json(url, options = {}) {
    const response = await request(url, {credentials:'same-origin', ...options});
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'We couldn’t update your cart. Please try again.');
    return data;
  }
  async function recommendations() {
    const cfg = config(), target = root.querySelector('[data-sc-recommendations-list]');
    if (!target || !cfg.recommendFor) return;
    const key = [base, cfg.recommendFor, cfg.recommendLimit, window.Shopify?.currency?.active].join(':');
    try {
      if (!recommendationCache.has(key)) {
        const pending = (async () => {
          const response = await request(`${base}recommendations/products?section_id=smart-cart-recommendations&product_id=${cfg.recommendFor}&limit=10&intent=related`);
          if (!response.ok) throw new Error('Recommendations unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const list = doc.querySelector('.smart-cart__recs');
          if (!list) return null;
          [...list.children].slice(cfg.recommendLimit || 2).forEach(el => el.remove());
          return list;
        })();
        recommendationCache.set(key, pending);
        if (recommendationCache.size > 12) recommendationCache.delete(recommendationCache.keys().next().value);
      }
      const list = await recommendationCache.get(key);
      if (!list?.children.length || !target.isConnected) return;
      target.replaceChildren(list.cloneNode(true)); target.parentElement.hidden = false;
      setBusy(busy);
    } catch (_) { recommendationCache.delete(key); }
  }
  async function syncGifts(cfg = config()) {
    const wanted = (cfg.tiers || []).filter(t => t.available && t.price === 0 && cfg.basis >= t.threshold && !declinedGifts.has(t.variantId));
    let changed = null;
    const updates = {};
    for (const line of cfg.giftLines || []) {
      const quantity = wanted.some(t => t.variantId === line.variantId) ? 1 : 0;
      if (line.quantity !== quantity) {
        updates[line.key] = quantity;
      }
    }
    if (Object.keys(updates).length) changed = await post('cart/update.js', {updates});
    const items = wanted.filter(t => !(cfg.giftLines || []).some(l => l.variantId === t.variantId)).map(t => ({id:t.variantId, quantity:1, properties:{_smart_cart_gift:'1'}}));
    if (items.length) changed = await post('cart/add.js', {items});
    return changed;
  }
  async function render(result) {
    const data = result?.sections?.['smart-cart'] ? result.sections : await json(`${base}?sections=smart-cart&_=${Date.now()}`);
    if (!data['smart-cart']) throw new Error('Your cart updated, but the preview could not refresh. Open the full cart to check it.');
    const doc = new DOMParser().parseFromString(data['smart-cart'], 'text/html');
    const body = doc.querySelector('[data-sc-body]'), cfg = doc.querySelector('[data-sc-config]');
    if (!body || !cfg) throw new Error('Please open the full cart to view your latest items.');
    const scrollTop = root.querySelector('.smart-cart__scroll')?.scrollTop || 0;
    root.querySelector('[data-sc-body]').replaceWith(body);
    root.querySelector('[data-sc-config]').replaceWith(cfg);
    setBusy(busy);
    root.querySelector('.smart-cart__scroll').scrollTop = scrollTop;
    document.querySelectorAll('[data-cart-count],.cart-count,.quick-cart__indicator-inner').forEach(el => { el.textContent = config().itemCount; el.closest('.js-cart-count')?.classList.toggle('visible', config().itemCount > 0); });
  }
  function pendingProduct(source, quantity = 1) {
    const item = source?.closest('.smart-cart__rec');
    const section = source?.closest('.shopify-section');
    const title = item?.querySelector('.smart-cart__rec-title')?.textContent?.trim()
      || section?.querySelector('h1')?.textContent?.trim() || 'Your selected product';
    const image = (item || section)?.querySelector('img');
    return {title, image: image?.currentSrc || image?.src, quantity};
  }
  function showPending(product) {
    root.classList.add('is-adding');
    const row = document.createElement('li');
    row.className = 'smart-cart__item smart-cart__pending';
    row.setAttribute('data-sc-pending', '');
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image; image.alt = ''; image.width = 72; image.height = 72;
      row.append(image);
    } else {
      row.append(document.createElement('span'));
    }
    const details = document.createElement('div');
    details.className = 'smart-cart__item-details';
    const title = document.createElement('strong');
    title.className = 'smart-cart__item-title'; title.textContent = product.title;
    const state = document.createElement('p');
    state.className = 'smart-cart__item-meta';
    state.setAttribute('role', 'status');
    state.textContent = 'Adding' + (product.quantity > 1 ? ' ' + product.quantity + ' items' : '') + '…';
    details.append(title, state); row.append(details);
    let list = root.querySelector('.smart-cart__items');
    if (!list) {
      list = document.createElement('ul'); list.className = 'smart-cart__items';
      list.setAttribute('data-sc-pending-list', '');
      root.querySelector('.smart-cart__scroll').prepend(list);
    }
    list.prepend(row);
    root.querySelector('.smart-cart__scroll').scrollTop = 0;
  }
  function clearPending() {
    root.classList.remove('is-adding');
    root.querySelectorAll('[data-sc-pending],[data-sc-pending-list]').forEach(el => el.remove());
  }
  async function run(action, shouldOpen = true, pending = null) {
    if (busy) return;
    if (shouldOpen) open();
    if (pending) showPending(pending);
    const focused = document.activeElement;
    const focusedLine = focused?.closest('[data-sc-line]')?.dataset.scLine;
    const qtyDirection = focused?.hasAttribute('data-sc-qty') ? (focused.textContent.trim() === '+' ? '+' : '−') : null;
    setBusy(true); message('', true); message(action ? 'Updating your cart…' : 'Loading your cart…');
    try {
      const result = action ? await action() : null;
      const section = result?.sections?.['smart-cart'];
      const updatedConfig = section && new DOMParser().parseFromString(section, 'text/html').querySelector('[data-sc-config]');
      if (updatedConfig) {
        if (pending) {
          await render(result);
          clearPending();
          message('Product added. Checking free gifts…');
        }
        const gifts = await syncGifts(JSON.parse(updatedConfig.textContent));
        if (gifts || !pending) await render(gifts || result);
      } else {
        await render(result);
        const gifts = await syncGifts();
        if (gifts) await render(gifts);
      }
      lastRefresh = Date.now();
      recommendations();
      message(action ? 'Cart updated.' : 'Cart loaded.');
    } catch (error) {
      // A failed render must not replay a successful add request.
      message(error.message || 'We couldn’t update your cart. Please try again or open the full cart.', true);
    } finally {
      clearPending();
      setBusy(false);
      if (root.classList.contains('is-open') && !document.activeElement?.closest('[data-smart-cart]')) {
        const line = [...root.querySelectorAll('[data-sc-line]')].find(el => el.dataset.scLine === focusedLine);
        const next = qtyDirection && line ? [...line.querySelectorAll('[data-sc-qty]')].find(el => el.textContent.trim() === qtyDirection) : null;
        (next || root.querySelector('button[data-sc-close]'))?.focus();
      }
    }
  }
  function post(endpoint, data) {
    return json(base + endpoint, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...data, sections:['smart-cart'], sections_url:base})});
  }
  panel.addEventListener('cancel', event => { event.preventDefault(); close(); });
  root.addEventListener('change', event => {
    const select = event.target.closest('[data-sc-plan]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const button = select.closest('[data-sc-subscription-options]').querySelector('[data-sc-apply-plan]');
    button.dataset.planId = select.value;
    button.textContent = option.dataset.buttonLabel;
    button.title = option.dataset.planName;
  });
  root.addEventListener('click', event => {
    if (event.target === panel) { const box = panel.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right) { close(); return; } }
    if (event.target.closest('[data-sc-close]')) { close(); return; }
    const planButton = event.target.closest('[data-sc-apply-plan]');
    if (planButton) {
      if (busy) return;
      const key = planButton.dataset.lineKey;
      const planId = planButton.dataset.planId ? Number(planButton.dataset.planId) : null;
      run(async () => {
        const cart = await json(base + 'cart.js');
        const index = cart.items.findIndex(item => item.key === key);
        if (index < 0) throw new Error('This item changed. Please refresh the cart and try again.');
        return post('cart/change.js', {line:index + 1, quantity:cart.items[index].quantity, selling_plan:planId});
      });
      return;
    }
    const gift = event.target.closest('[data-sc-remove-gift]');
    if (gift) {
      if (busy) return;
      declinedGifts.add(Number(gift.dataset.variantId));
      run(() => post('cart/change.js', {id:gift.dataset.scRemoveGift, quantity:0}));
      return;
    }
    const button = event.target.closest('[data-sc-qty],[data-sc-add]');
    if (!button) return;
    event.preventDefault();
    if (busy) return;
    if (button.hasAttribute('data-sc-add')) run(() => post('cart/add.js', {items:[{id:Number(button.dataset.scAdd), quantity:1}]}), true, pendingProduct(button));
    else {
      const line = button.closest('[data-sc-line]');
      run(() => post('cart/change.js', {id:line.dataset.scLine, quantity:Number(button.dataset.scQty)}));
    }
  });
  window.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || link.closest('[data-smart-cart]') || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && url.pathname.replace(/\/$/,'') === `${base}cart`.replace(/\/$/,'')) {
      event.preventDefault(); event.stopImmediatePropagation(); showCart();
    }
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, location.href).pathname)) return;
    // Keep express checkout and app-specific accelerated flows under Shopify's control.
    if (event.submitter?.name === 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy) return;
    const data = new FormData(form);
    data.set('sections', 'smart-cart');
    data.set('sections_url', base);
    const submitter = event.submitter;
    if (submitter) submitter.disabled = true;
    run(() => json(base + 'cart/add.js', {method:'POST', body:data}), true, pendingProduct(form, Math.max(1, Number(data.get('quantity')) || 1))).finally(() => { if (submitter) submitter.disabled = false; });
  }, true);
  document.addEventListener('keydown', event => {
    if (!root.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]')].filter(el => el.getClientRects().length);
    const first = focusable[0], last = focusable[focusable.length-1];
    if (!first) { event.preventDefault(); panel.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  document.addEventListener('flu:cart:item-added', () => run(null));
  document.addEventListener('cart:refresh', () => run(null, root.classList.contains('is-open')));
  window.addEventListener('pageshow', event => { if (event.persisted) run(null, false); });
  function showCart() {
    open();
    if (!lastRefresh || Date.now() - lastRefresh > 30000) return run(null);
    recommendations();
    return Promise.resolve();
  }
  window.addEventListener('focus', () => { lastRefresh = 0; });
  window.PristineCart = {open:showCart, close, refresh:() => run(null, false)};
  document.documentElement.classList.add('pristine-cart-ready');
})();
